/**
 * Correcting or overriding recorded hours (M2, §§1.15, 1.16).
 *
 * One control for two operations, because staff arrive at both the same way —
 * *"this number is wrong"* — and only then does it matter which kind of wrong:
 *
 *   **Correction** — it was mistyped. The record is fixed, nothing is flagged.
 *   **Override**   — it was right, and we are changing it anyway. The record is
 *                    flagged so §5.42 can find every manually adjusted one.
 *
 * Making staff choose between them, with the difference spelled out, is the
 * point. A single "edit hours" box would collapse the distinction and
 * `override_flag` would either never be set or always be.
 *
 * Neither touches the status. An approved record stays approved with new hours —
 * Plan D2 rejected a `CORRECTED` status precisely so that is representable.
 */

import { useState } from 'react';
import { formatDuration, fromMinutes } from '@parinaam-vms/shared';
import type { AttendanceDetail } from '@parinaam-vms/shared';
import { Button } from '../common/Button';
import { DurationInput } from '../forms/DurationInput';
import { SelectInput, TextArea } from '../forms/Field';
import { useCorrectAttendance, useOverrideAttendance } from '../../hooks/useAttendance';
import styles from './AdjustHours.module.css';

type Kind = 'correction' | 'override';

export interface AdjustHoursProps {
  record: AttendanceDetail;
  onClose: () => void;
}

export function AdjustHours({ record, onClose }: AdjustHoursProps) {
  const [kind, setKind] = useState<Kind>('correction');
  const [minutes, setMinutes] = useState(record.duration_minutes);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const correct = useCorrectAttendance();
  const override = useOverrideAttendance();
  const pending = correct.isPending || override.isPending;
  const failure = (correct.error ?? override.error) as Error | null;

  const submit = () => {
    const problems: Record<string, string> = {};
    if (minutes === record.duration_minutes) {
      problems.duration = `That is the same figure — ${formatDuration(record.duration_minutes)} already.`;
    }
    // The same rule the API and the database apply (§4.28, §8.40).
    if (reason.trim().length < 5)
      problems.reason = 'Please give a reason of at least 5 characters.';

    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      return;
    }

    if (kind === 'override') {
      override.mutate(
        { id: record.attendance_id, minutes, reason: reason.trim() },
        { onSuccess: onClose }
      );
      return;
    }
    correct.mutate(
      { id: record.attendance_id, body: { duration_minutes: minutes, reason: reason.trim() } },
      { onSuccess: onClose }
    );
  };

  const { hours, minutes: mins } = fromMinutes(record.duration_minutes);

  return (
    <div className={styles.panel}>
      <p className={styles.current}>
        Recorded: <strong>{formatDuration(record.duration_minutes)}</strong> ({hours}h {mins}m) for{' '}
        {record.volunteer_name} on {record.activity_name}.
        {record.status === 'APPROVED' && (
          <> This record is approved; changing the hours will not un-approve it.</>
        )}
      </p>

      {failure && (
        <p className={styles.error} role="alert">
          {failure.message}
        </p>
      )}

      <SelectInput
        label="What kind of change is this?"
        placeholder=""
        options={[
          { value: 'correction', label: 'Correction — the figure was entered wrongly' },
          { value: 'override', label: 'Override — the figure was right, we are changing it' },
        ]}
        value={kind}
        onChange={(event) => setKind(event.target.value as Kind)}
      />

      <DurationInput
        label="Corrected duration"
        required
        value={minutes}
        error={errors.duration}
        onChange={(value) => {
          setMinutes(value);
          setErrors((current) => ({ ...current, duration: '' }));
        }}
      />

      <TextArea
        label={kind === 'override' ? 'Why are the hours being overridden?' : 'What was wrong?'}
        required
        rows={2}
        value={reason}
        error={errors.reason}
        onChange={(event) => {
          setReason(event.target.value);
          setErrors((current) => ({ ...current, reason: '' }));
        }}
      />

      <div className={styles.actions}>
        <Button variant="primary" loading={pending} onClick={submit}>
          {kind === 'override' ? 'Override the hours' : 'Save the correction'}
        </Button>
        <Button onClick={onClose} disabled={pending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
